import { useState, useCallback, RefObject } from "react"

interface UseZoomPanProps {
  svgWrapperRef: RefObject<HTMLDivElement>
  containerRef: RefObject<HTMLDivElement>
}

export const useZoomPan = ({ svgWrapperRef, containerRef }: UseZoomPanProps) => {
  const [zoom, setZoom] = useState(100)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })

  const constrainPan = useCallback(
    (newPan: { x: number; y: number }, currentZoom: number): { x: number; y: number } => {
      if (!svgWrapperRef.current || !containerRef.current) return newPan

      const wrapper = svgWrapperRef.current.parentElement
      if (!wrapper) return newPan

      const container = containerRef.current
      const svgElement = container.querySelector("svg")
      if (!svgElement) return newPan

      const containerRect = wrapper.getBoundingClientRect()
      const svgRect = svgElement.getBoundingClientRect()

      const scaledWidth = svgRect.width * (currentZoom / 100)
      const scaledHeight = svgRect.height * (currentZoom / 100)

      const maxX = Math.max(0, (containerRect.width - scaledWidth) / 2)
      const minX = Math.min(0, containerRect.width - scaledWidth - maxX)
      const maxY = Math.max(0, (containerRect.height - scaledHeight) / 2)
      const minY = Math.min(0, containerRect.height - scaledHeight - maxY)

      return {
        x: Math.max(minX, Math.min(maxX, newPan.x)),
        y: Math.max(minY, Math.min(maxY, newPan.y)),
      }
    },
    [svgWrapperRef, containerRef],
  )

  const handleZoomIn = useCallback(() => {
    if (!svgWrapperRef.current) return
    const wrapper = svgWrapperRef.current.parentElement
    if (!wrapper) return
    const rect = wrapper.getBoundingClientRect()
    const centerX = rect.width / 2
    const centerY = rect.height / 2

    setZoom((prevZoom) => {
      const newZoom = Math.min(prevZoom + 25, 400)
      const scale = newZoom / prevZoom

      setPan((prevPan) => {
        const newPan = {
          x: centerX - (centerX - prevPan.x) * scale,
          y: centerY - (centerY - prevPan.y) * scale,
        }
        return constrainPan(newPan, newZoom)
      })

      return newZoom
    })
  }, [svgWrapperRef, constrainPan])

  const handleZoomOut = useCallback(() => {
    if (!svgWrapperRef.current) return
    const wrapper = svgWrapperRef.current.parentElement
    if (!wrapper) return
    const rect = wrapper.getBoundingClientRect()
    const centerX = rect.width / 2
    const centerY = rect.height / 2

    setZoom((prevZoom) => {
      const newZoom = Math.max(prevZoom - 25, 100)
      const scale = newZoom / prevZoom

      setPan((prevPan) => {
        const newPan = {
          x: centerX - (centerX - prevPan.x) * scale,
          y: centerY - (centerY - prevPan.y) * scale,
        }
        return constrainPan(newPan, newZoom)
      })

      return newZoom
    })
  }, [svgWrapperRef, constrainPan])

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return
      setIsDragging(true)
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y })
    },
    [pan],
  )

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) return
      const newPan = {
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      }
      setPan(constrainPan(newPan, zoom))
    },
    [isDragging, dragStart, constrainPan, zoom],
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length !== 1) return
      const touch = e.touches[0]
      setIsDragging(true)
      setDragStart({ x: touch.clientX - pan.x, y: touch.clientY - pan.y })
    },
    [pan],
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging || e.touches.length !== 1) return
      const touch = e.touches[0]
      const newPan = {
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y,
      }
      setPan(constrainPan(newPan, zoom))
    },
    [isDragging, dragStart, constrainPan, zoom],
  )

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault()
      if (!svgWrapperRef.current) return

      const wrapper = svgWrapperRef.current.parentElement
      if (!wrapper) return
      const rect = wrapper.getBoundingClientRect()
      const mouseX = e.clientX - rect.left
      const mouseY = e.clientY - rect.top

      setZoom((prevZoom) => {
        const delta = Math.sign(e.deltaY) * Math.min(Math.abs(e.deltaY) / 4, 25)
        const newZoom = Math.max(100, Math.min(400, prevZoom - delta))
        const scale = newZoom / prevZoom

        setPan((prevPan) => {
          const newPan = {
            x: mouseX - (mouseX - prevPan.x) * scale,
            y: mouseY - (mouseY - prevPan.y) * scale,
          }
          return constrainPan(newPan, newZoom)
        })

        return newZoom
      })
    },
    [svgWrapperRef, constrainPan],
  )

  return {
    zoom,
    pan,
    isDragging,
    handleZoomIn,
    handleZoomOut,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleWheel,
  }
}
